import { useBuilderActions, useTree } from '@customerio/dryad-react';
import type { GroupNode } from '@customerio/dryad';
import { addChildGroup, ungroupNode } from './group-rows';

/**
 * The two group operations that need to read the tree as well as mutate it.
 *
 * Both need a node the mutation alone cannot name: flipping an appended
 * group's operator needs the id the append assigned, and ungrouping needs the
 * group's parent and its position among its siblings.
 *
 * `getLiveTree` is why it is a parameter rather than another context read.
 * `addChildGroup` reads the tree *after* its own append, and every tree read
 * dryad-react offers is a subscription: the value a render closed over is the
 * tree as it was, one child short of the group being looked for, and React
 * has not re-rendered yet inside the click that appended it. The surface that
 * allows nesting holds the builder, so it passes `builder.getTree` — the same
 * synchronous read the mutation itself used. Ungrouping needs only the tree on
 * screen, so it falls back to the subscribed one when no reader is given.
 */
export function useGroupTreeActions(getLiveTree?: () => GroupNode) {
  const tree = useTree();
  const actions = useBuilderActions();
  const readTree = getLiveTree ?? (() => tree);

  return {
    /** Appends a nested group joined by the given operator. */
    addGroup: (parentId: string, operator: 'and' | 'or') => {
      addChildGroup(actions, readTree, parentId, operator);
    },
    /** Removes a group, lifting its conditions into the parent in its place. */
    ungroup: (groupId: string) => {
      ungroupNode(actions, readTree(), groupId);
    },
  };
}
